import { OpenInNew } from '@mui/icons-material';
import { IconButton, Link, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { FC, useMemo } from 'react';

import { formatKundeAdresse } from '../../../components/formatUtils';
import { formatDate } from '../../../components/formatUtils/dateFormatUtils';
import { AppDataGrid } from '../../../components/table/AppDataGrid';
import {
  getInteressentenStatusLabel,
  getVertriebsStatus,
  Interessent,
  InteressentenStatus,
} from '../../kunde/api/types';

export const interessentTableColumns: GridColDef[] = [
  { field: 'organisation', headerName: 'Organisation', flex: 2 },
  {
    field: 'operativerAnsprechpartner',
    headerName: 'Operativer Ansprechpartner',
    flex: 2,
  },
  {
    field: 'adresse',
    headerName: 'Adresse',
    flex: 2,
    valueGetter: (_value, row) => formatKundeAdresse(row as Interessent),
  },
  {
    field: 'email',
    headerName: 'E-Mail',
    flex: 2,
    renderCell: params =>
      params.value ? (
        <Link onClick={event => event.stopPropagation()} href={`mailto:${params.value}`}>
          {params.value}
        </Link>
      ) : null,
  },
  {
    field: 'telefonnummer',
    headerName: 'Telefonnummer',
    flex: 1.5,
  },
  {
    field: 'vertriebsStatus',
    headerName: 'Status',
    flex: 1.5,
    valueGetter: (value: InteressentenStatus | null) => getInteressentenStatusLabel(value),
  },
  { field: 'angebotsnummer', headerName: 'Angebotsnummer', flex: 1.5 },
  {
    field: 'angebotslink',
    headerName: 'Angebot',
    width: 90,
    sortable: false,
    renderCell: params =>
      params.value ? (
        <Tooltip title="Angebot öffnen">
          <IconButton
            aria-label="Angebot öffnen"
            component="a"
            href={params.value}
            target="_blank"
            rel="noreferrer"
            onClick={event => event.stopPropagation()}
            size="small"
          >
            <OpenInNew fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null,
  },
  {
    field: 'zuletztBearbeitet',
    headerName: 'Zuletzt bearbeitet',
    flex: 1.5,
    type: 'date',
    valueFormatter: value => formatDate(value as string),
  },
  {
    field: 'creationDate',
    headerName: 'Angelegt am',
    flex: 1.5,
    type: 'date',
    valueFormatter: value => formatDate(value as string),
  },
];

interface InteressentTableProps {
  /** Bereits nach der Suche gefiltert — dieselbe Liste, auf der auch der Funnel zählt. */
  interessenten: Interessent[];
  isFetching: boolean;
  status: InteressentenStatus | '';
}

export const InteressentTable: FC<InteressentTableProps> = ({
  interessenten,
  isFetching,
  status,
}) => {
  const rows = useMemo(
    () =>
      status
        ? interessenten.filter(
            interessent => getVertriebsStatus(interessent.vertriebsStatus) === status
          )
        : interessenten,
    [interessenten, status]
  );

  return (
    <AppDataGrid
      checkboxSelection={true}
      loading={isFetching}
      columns={interessentTableColumns}
      rows={rows}
    />
  );
};
